import { SupabaseClient } from '@supabase/supabase-js';
import { Season } from '@/utils/seasons/seasonHelpers';

/**
 * Today, as the person's own device reckons it.
 *
 * ONLY correct in a browser (#3911). `hasSeasonEnded` used to read a bare
 * `new Date()` for every caller, which is right for a staffer sitting in the
 * gym and wrong for a server: Vercel runs in UTC, so from about 8pm Eastern on
 * a season's last day the server called the season over while the gym was
 * still in it — refusing a practice or a tryout the screen still accepted.
 *
 * The two checks below now REQUIRE a "today" rather than picking one, so no
 * caller can inherit the wrong clock by saying nothing. Server callers pass
 * `getTodayIsoDateInTimezone(await getOrgTimezone(...))`; browser callers pass
 * this.
 *
 * `hasSeasonEnded` used to compare local midnights with
 * `Math.ceil(diff / 86400000) < 0`, which disagreed with `end_date < today` on
 * the day after a spring-forward (a 23-hour gap rounds to `-0`). It now counts
 * calendar days, so the two rules agree on every day (#3927).
 *
 * The browser is still not the gym's clock — a coach at an out-of-state
 * competition reads their own — but that is one person who has moved, on the
 * gym's last day, in the wrong direction. The server was every gym, every
 * evening. Deliberately left as is.
 */
export const todayInViewerZone = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Check if a season has ended by season ID
 * Fetches season from seasons_computed view and checks end_date
 *
 * @param supabase - Supabase client instance
 * @param seasonId - Season ID to check
 * @param todayInGymZone - `YYYY-MM-DD` for the day the caller means. Required
 *   on purpose — see `todayInViewerZone` above.
 * @returns Object with hasEnded boolean and season data
 */
export async function checkSeasonEndedById(
  supabase: SupabaseClient,
  seasonId: string,
  todayInGymZone: string
): Promise<{ hasEnded: boolean; season: Season | null; error?: string }> {
  try {
    const { data: season, error } = await supabase
      .from('seasons_computed')
      .select('id, name, start_date, end_date, season_status')
      .eq('id', seasonId)
      .single();

    if (error || !season) {
      return {
        hasEnded: false,
        season: null,
        error: error?.message || 'Season not found'
      };
    }

    const seasonForCheck: Season = {
      id: season.id,
      name: season.name,
      start_date: season.start_date,
      end_date: season.end_date,
      season_status: season.season_status as any,
      is_active: season.season_status === 'current',
      organization_id: '' // Not needed for the ended check
    };

    return {
      hasEnded: seasonHasEndedAsOf(seasonForCheck, todayInGymZone),
      season: seasonForCheck
    };
  } catch (error) {
    return {
      hasEnded: false,
      season: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Check if a team's season has ended
 * 
 * @param supabase - Supabase client instance
 * @param teamId - Team ID to check
 * @param todayInGymZone - `YYYY-MM-DD` for the day the caller means. Required
 *   on purpose — see `todayInViewerZone` above.
 * @returns Object with hasEnded boolean and season data
 */
export async function checkTeamSeasonEnded(
  supabase: SupabaseClient,
  teamId: string,
  todayInGymZone: string
): Promise<{ hasEnded: boolean; season: Season | null; teamSeasonId: string | null; error?: string }> {
  try {
    // Get team's season_id
    const { data: team, error: teamError } = await supabase
      .from('teams')
      .select('season_id')
      .eq('id', teamId)
      .single();

    if (teamError || !team || !team.season_id) {
      return {
        hasEnded: false,
        season: null,
        teamSeasonId: null,
        error: teamError?.message || 'Team not found or has no season'
      };
    }

    // Check if season has ended
    const seasonCheck = await checkSeasonEndedById(supabase, team.season_id, todayInGymZone);
    
    return {
      hasEnded: seasonCheck.hasEnded,
      season: seasonCheck.season,
      teamSeasonId: team.season_id
    };
  } catch (error) {
    return {
      hasEnded: false,
      season: null,
      teamSeasonId: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}



/**
 * Which season governs an event created without a browser season picker (#3893).
 *
 * The create-event screens gate on the season the staffer has SELECTED. The
 * assistant's `create_event` tool has no such selection, so it resolves the
 * season from the event's own date instead (owner decision, 2026-08-22): a
 * practice dated inside a closed season is refused, one dated into the next
 * season is allowed.
 *
 * It FAILS OPEN wherever the screens do — no seasons, an unreadable list, a
 * date that falls in a gap while the gym still has an open season. A brand-new
 * gym with no seasons at all must never be blocked from adding events.
 *
 * The one gap that does block is "every season this gym has is over": that is
 * the same state the screen refuses in, and it is the common one — a gym past
 * its season end that has not created the next one yet.
 *
 * TWO THINGS THIS FUNCTION IS DELIBERATE ABOUT, both found in review:
 *
 * 1. "TODAY" IS THE GYM'S TODAY, NOT THE SERVER'S. `hasSeasonEnded` reads a bare
 *    `new Date()`, which is correct in a browser sitting in the staffer's own
 *    zone and wrong on a server, because Vercel runs in UTC: from about 8pm
 *    Eastern on a season's last day the server calls it over while the gym is
 *    still in it. `seasonHasEndedAsOf` applies the same rule
 *    (`end_date < today`) against a today resolved in the gym's zone.
 *    `__tests__/seasonHasEndedAsOf.test.ts` feeds both the same day across a
 *    window around today, across a DST transition, and they agree on every
 *    day (#3927).
 *
 *    The other two server-side callers — /api/teams/[id]/schedule and
 *    /api/tryout-events, via `checkSeasonEndedById` in this same file — carried
 *    the same evening off-by-one until #3911 closed it. Both checks now REQUIRE
 *    a `todayInGymZone` rather than reading a clock of their own, so a caller
 *    can no longer inherit the wrong one by saying nothing.
 *
 * 2. WHICH ADVICE THE REFUSAL GIVES IS DECIDED BY WHETHER AN OPEN SEASON EXISTS,
 *    not by whether one covered the date. The screen's second sentence works
 *    that way (CreateEventClient.tsx — it offers a switch when a usable season
 *    exists, and "ask an owner or admin to create the new season" when none
 *    does). Deciding it by coverage told a gym whose only season is over to
 *    "pick a date in a season that has not ended" when there was no such season
 *    to pick.
 *
 * Dates compare as plain `YYYY-MM-DD` strings, which is exact and carries no
 * timezone of its own — both the event's date and today are resolved in the
 * gym's zone before they get here.
 */
export type EventDateSeasonCheck = {
  blocked: boolean;
  /** The season the refusal is about. Null whenever `blocked` is false. */
  season: Season | null;
  /**
   * 'date-in-ended-season' — the date sits in a season that is over, but the gym
   *                          has another season still open to move it into.
   * 'no-open-season'       — every season this gym has is over.
   */
  reason: 'date-in-ended-season' | 'no-open-season' | null;
};

const OPEN = (): EventDateSeasonCheck => ({ blocked: false, season: null, reason: null });

/** `YYYY-MM-DD` off a date or timestamp column, so both sides of every
 *  comparison in here are the same shape. */
const dayOf = (value: string): string => String(value).slice(0, 10);

/**
 * `hasSeasonEnded`'s rule — end date strictly in the past, so a season ending
 * today does NOT block today — measured against a caller-supplied today rather
 * than the server's clock. See point 1 on the block comment above.
 */
export const seasonHasEndedAsOf = (season: Season, todayInGymZone: string): boolean =>
  dayOf(season.end_date) < todayInGymZone;

export async function checkSeasonEndedForEventDate(
  supabase: SupabaseClient,
  organizationId: string,
  eventDate: string,
  todayInGymZone: string
): Promise<EventDateSeasonCheck> {
  const isDay = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v || '');
  if (!organizationId || !isDay(eventDate) || !isDay(todayInGymZone)) return OPEN();

  try {
    const { data, error } = await supabase
      .from('seasons_computed')
      .select('id, name, start_date, end_date, season_status, organization_id')
      .eq('organization_id', organizationId)
      .neq('season_status', 'archived')
      // All-star gyms run overlapping seasons — next season's tryouts start
      // before this one ends — so a date can sit inside two. What actually
      // makes that deterministic is preferring an OPEN one below; measured,
      // removing this ORDER BY changes no answer the tests can see. It is here
      // so the row the refusal names is the newest of equals rather than
      // whichever the view happened to hand back.
      .order('start_date', { ascending: false });

    if (error || !data) return OPEN();

    const seasons = (data as any[])
      .filter((s) => s?.id && s?.start_date && s?.end_date)
      .map<Season>((s) => ({
        id: s.id,
        name: s.name,
        start_date: s.start_date,
        end_date: s.end_date,
        season_status: s.season_status,
        is_active: s.season_status === 'current',
        organization_id: s.organization_id || organizationId,
      }));

    if (seasons.length === 0) return OPEN();

    const hasOpenSeason = seasons.some((s) => !seasonHasEndedAsOf(s, todayInGymZone));
    const reason = hasOpenSeason ? 'date-in-ended-season' : 'no-open-season';

    const covering = seasons.filter(
      (s) => dayOf(s.start_date) <= eventDate && eventDate <= dayOf(s.end_date)
    );
    if (covering.length > 0) {
      // An open season covering the date wins over an ended one covering it too.
      const open = covering.find((s) => !seasonHasEndedAsOf(s, todayInGymZone));
      if (open) return OPEN();
      return { blocked: true, season: covering[0], reason };
    }

    // No season covers this date. Fine as long as the gym still has one open —
    // staff plan into gaps between seasons all the time.
    if (hasOpenSeason) return OPEN();

    // Every season is over. Name the one that ended most recently, which is the
    // one the screen would have had selected.
    const mostRecent = seasons.reduce((a, b) => (dayOf(a.end_date) >= dayOf(b.end_date) ? a : b));
    return { blocked: true, season: mostRecent, reason: 'no-open-season' };
  } catch {
    return OPEN();
  }
}
